import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { patchGenerativeModel, getGenerativeModels } from 'entities/generative-models/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useCustomModelStore } from 'widgets/customModel/state';
import ModelSettings from 'components/model-settings';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

const GenerativeModelSettings: FC = () => {
	const { t } = useTranslation();
	const { generativeModelId } = useParams();

	const { generativeModels, setGenerativeModels } = useCustomModelStore();

	const currentCustomModel = useMemo(
		() => generativeModels?.find((item) => item._id === generativeModelId),
		[generativeModels, generativeModelId],
	);

	const { refetch } = useQuery({
		queryKey: ['get/generative-models'],
		queryFn: () => getGenerativeModels(),
		onSuccess: (response) => {
			setGenerativeModels(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate, isLoading } = useMutation({
		mutationKey: ['patch/generative-settings'],
		mutationFn: patchGenerativeModel,
		onSuccess: async () => {
			await refetch();
			enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	if (!currentCustomModel || !generativeModelId) return null;

	return (
		<PageWrapper>
			<ModelSettings
				isLoading={isLoading}
				modelSettings={{
					name: currentCustomModel?.name,
					settings: currentCustomModel?.settings,
				}}
				onSubmit={({ id, data }) => mutate({ generativeModelId: id, data })}
				modelId={generativeModelId}
				description={t('generative_model.description_settings')}
			/>
		</PageWrapper>
	);
};

export default GenerativeModelSettings;
